import { Button, Group, LoadingOverlay, Select, Stack, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import type { JSX } from "react";
import { useSiteConfig } from "../SiteConfig";

export interface IFormValues {
    name: string;
    text: string;
    voice: string;
    instructions: string;
}

interface IProps {
    handleSubmit: (values: IFormValues) => void;
    initialValues?: IFormValues;
    fetching: boolean;
}

function TtsProjectForm({ handleSubmit, initialValues, fetching }: IProps): JSX.Element {
    const config = useSiteConfig();
    const defaultVoice = config.ttsVoices[0] ?? "alloy";

    const form = useForm<IFormValues>({
        initialValues: initialValues ?? {
            name: "",
            text: "",
            voice: defaultVoice,
            instructions: "",
        },
        validate: {
            name: (value) => (value ? null : "Required"),
            text: (value) => (value ? null : "Required"),
            voice: (value) => (value ? null : "Required"),
        },
    });

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack maw={800} mx="auto">
                <LoadingOverlay visible={fetching} />
                <TextInput withAsterisk label="Name" {...form.getInputProps("name")} />
                <Textarea
                    withAsterisk
                    label="Text"
                    description="Enter the text to convert to speech. Paragraphs will be split into separate blocks."
                    autosize
                    minRows={5}
                    maxRows={20}
                    {...form.getInputProps("text")}
                />
                <Select
                    label="Voice"
                    withAsterisk
                    data={config.ttsVoices.map((v) => ({ value: v, label: v }))}
                    {...form.getInputProps("voice")}
                />
                <Textarea
                    label="Instructions"
                    description="Optional instructions for the voice (e.g., accent, tone, speed, emotional range)"
                    autosize
                    minRows={2}
                    maxRows={5}
                    {...form.getInputProps("instructions")}
                />
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Create
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default TtsProjectForm;
